import Bugsnag from '@bugsnag/js'
import {
  Button,
  Grid,
  Link,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { client } from '../utils/client'
import { FormType } from '../utils/sharedTypes'

type Props = {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  open: boolean
  form: FormType
}

const formatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })

export default function BankTransferModal({ form, open }: Props) {
  const [magicLink, setMagicLink] = useState<string | null | undefined>(undefined)
  const MAX_RETRY = 6
  const PAUSE_BETWEEN_RETRY = 5000
  const RETRY = useRef(0)
  useEffect(() => {
    if (form.data.registration_type === 'PreConfiguredRegistration') {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setTimeout(() => {}, PAUSE_BETWEEN_RETRY)

      const interval = setInterval(() => {
        if (RETRY.current >= MAX_RETRY) {
          clearInterval(interval)
          if (!magicLink) {
            setMagicLink(null)
          }
          return
        }

        client
          .get(`/registration/${form.data.global_id}/magic_link/`)
          .then((resp) => {
            const data = resp.data as { magic_link: string | null }

            if (data.magic_link === null) {
              setMagicLink(null)
            } else if (data.magic_link !== 'pending') {
              setMagicLink(data.magic_link)
              clearInterval(interval)
            }
          })
          .catch((err: Error) => {
            Bugsnag.notify(err)
            console.error(err)
          })
          .finally(() => {
            RETRY.current++
          })
      }, PAUSE_BETWEEN_RETRY)
    }
  }, [form.data.registration_type, RETRY.current])

  return (
    <Modal
      open={open}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Grid
        container
        item
        xs={12}
        md={6}
        flexDirection="column"
        sx={{
          background: 'white',
          padding: '20px',
        }}
      >
        <Grid>
          <TableContainer sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Payer par virement bancaire
            </Typography>
            <Typography sx={{ marginBottom: 1 }}>
              Vous trouverez ci-dessous les informations bancaires pour procéder au virement, un email vous a aussi été
              envoyé avec ces informations.
            </Typography>
            <Typography sx={{ marginBottom: 1 }}>
              En parallèle, <b>nous avons déjà créé votre compte sur notre plateforme d'apprentissage</b>, vous pouvez
              dès à présent y accéder en cliquant sur le lien ci-dessous.
            </Typography>
            <Typography sx={{ marginBottom: 1 }}></Typography>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Compte bancaire</TableCell>
                  <TableCell align="center">Référence</TableCell>
                  <TableCell align="right">Montant</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0 }}>
                    <Typography>
                      <b>Titulaire :</b> ELOCE
                      <br />
                      <b>RIB :</b> FR76 1695 8000 0166 3296 9110 493
                      <br />
                      <b>BIC :</b> QNTOFRP1XXX
                    </Typography>
                    <Typography variant="body2" sx={{ marginTop: 0.5 }}>
                      <Link
                        href="https://walter-resources.s3.amazonaws.com/cgu_cgv/RIB Walter Learning.pdf"
                        target="_blank"
                      >
                        Télécharger le RIB en PDF
                      </Link>
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ border: 0 }}>
                    <Typography>INSC-{form.data.id}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    <Typography>
                      {formatter.format(form.data.personal_funding || form.data.default_personal_funding || 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item>
          <Grid
            container
            item
            justifyContent="center"
            sx={{
              mt: -1,
              mb: 2,
            }}
          >
            {magicLink ? (
              <Button
                size="large"
                variant="contained"
                component="a"
                href={magicLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Accéder à mon compte
              </Button>
            ) : (
              <Button size="large" variant="contained" disabled>
                {magicLink === undefined ? 'Création en cours...' : 'Lien non disponible'}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  )
}
